/**
 * What the dashboard shows for each industry.
 *
 * The company's industry is chosen in the setup wizard and stored as the
 * `company_industry` config key; AuthContext exposes it. A school needs fee
 * collection front and centre, a trading business needs stock and gross
 * margin, an NGO needs grant/project burn — showing every business the same
 * ten KPIs buries the numbers that actually matter to it.
 *
 * Each entry declares:
 *   label      — what to call this dashboard
 *   kpis       — [{ key, label, type? }] read from `summary`
 *   charts     — [{ id, col }] chosen from the CHARTS registry below
 *   pairedChart— the chart that shares a row with the recent-documents table
 *   quickLinks — the pages this industry reaches for most often
 *
 * `key` names come straight from the backend summary payload
 * (model\Accounting\Reports\DashboardOverview), so adding a KPI is a matter
 * of exposing the field there and naming it here.
 */

/** Series colours. Only charts are coloured now — KPI tiles and quick links
 *  are deliberately monochrome. */
const C = {
  green: "#10b981",
  red: "#ef4444",
  indigo: "#6366f1",
  amber: "#f59e0b",
  pink: "#ec4899",
  sky: "#0ea5e9",
  violet: "#8b5cf6",
  teal: "#14b8a6",
  orange: "#f97316",
  slate: "#64748b",
};

/** KPI shorthands reused across industries. A tile is just a label and a
 *  figure — the value carries the meaning, not decoration around it. */
const K = {
  revenue:     { key: "total_revenue",         label: "Total Revenue" },
  expenses:    { key: "total_expenses",        label: "Total Expenses" },
  profit:      { key: "net_profit",            label: "Net Profit / Loss" },
  margin:      { key: "profit_margin",         label: "Profit Margin", type: "percent" },
  cash:        { key: "cash_balance",          label: "Cash & Bank" },
  ar:          { key: "outstanding_ar",        label: "Unpaid Receivables" },
  ap:          { key: "outstanding_ap",        label: "Unpaid Payables" },
  overdueAr:   { key: "overdue_ar",            label: "Overdue Receivables" },
  liabilities: { key: "total_liabilities",     label: "Total Liabilities" },
  workingCap:  { key: "working_capital",       label: "Working Capital" },
  journals:    { key: "journals_this_month",   label: "Journals This Month", type: "count" },
  drafts:      { key: "draft_entries",         label: "Draft Entries", type: "count" },
  employees:   { key: "active_employees",      label: "Active Staff", type: "count" },

  // School
  feesBilled:      { key: "fees_billed",       label: "Fees Billed" },
  feesCollected:   { key: "fees_collected",    label: "Fees Collected" },
  feesOutstanding: { key: "fees_outstanding",  label: "Fees Outstanding" },
  collectionRate:  { key: "collection_rate",   label: "Collection Rate", type: "percent" },
  students:        { key: "active_students",   label: "Active Students", type: "count" },
  openBills:       { key: "open_bills",        label: "Open Fee Bills", type: "count" },
  feesWaived:      { key: "fees_waived",       label: "Fees Waived" },
  // Enrolment for the term actually running, not everyone on the register.
  termStudents:    { key: "students_this_term", label: "Students This Term", type: "count" },
  // Leavers who still owe. The amount is what makes this worth chasing, so it
  // rides along under the count, and the tile links to the list itself.
  archivedOwing:   {
    key: "archived_owing",
    label: "Archived Owing Fees",
    type: "count",
    sub: { key: "archived_owing_amount" },
    link: "/accounting/fees/progression?tab=archive&owing=1",
    linkLabel: "View",
  },

  // Stock-based
  stockValue:  { key: "stock_value",           label: "Stock on Hand" },
  lowStock:    { key: "low_stock_items",       label: "Items to Reorder", type: "count" },
  cogs:        { key: "cogs",                  label: "Cost of Sales" },
  grossProfit: { key: "gross_profit",          label: "Gross Profit" },
  grossMargin: { key: "gross_margin",          label: "Gross Margin", type: "percent" },

  // Project-based
  projects:      { key: "active_projects",     label: "Active Projects", type: "count" },
  projectBudget: { key: "total_project_budget", label: "Committed Budget" },
};

/**
 * Chart registry. `source` says where the series lives in the payload:
 * "charts" for the standard set, "extra" for the industry blocks.
 */
export const CHARTS = {
  rve:        { source: "charts", id: "rve",  title: "Revenue vs Expenses (Monthly)", kind: "bar",   series: [["Revenue", "revenue", C.green], ["Expenses", "expenses", C.red]], categories: "months" },
  npt:        { source: "charts", id: "npt",  title: "Net Profit Trend",              kind: "area",  series: [["Net Profit", "profit", C.indigo]], categories: "months" },
  cfm:        { source: "charts", id: "cfm",  title: "Monthly Cash Flow",             kind: "bar",   series: [["Inflow", "inflow", C.sky], ["Outflow", "outflow", C.orange]], categories: "months" },
  // Only the school layout still uses this: elsewhere the "Revenue by source"
  // panel carries the same breakdown, as a donut plus exact ranked figures.
  rb:         { source: "charts", id: "rb",   title: "Revenue Breakdown",             kind: "donut", valueKey: "values", categories: "labels" },
  eb:         { source: "charts", id: "eb",   title: "Where the Money Goes",          kind: "donut", valueKey: "values", categories: "labels" },
  ar:         { source: "charts", id: "ar",   title: "Receivables Aging",             kind: "bar",   series: [["Receivable", "amounts", C.amber]], categories: "buckets" },
  ap:         { source: "charts", id: "ap",   title: "Payables Aging",                kind: "bar",   series: [["Payable", "amounts", C.pink]], categories: "buckets" },
  inv:        { source: "charts", id: "inv",  title: "Invoice Status",                kind: "donut", valueKey: "counts", categories: "labels", money: false },
  tc:         { source: "charts", id: "tc",   title: "Top Customers (by billed value)", kind: "hbar", series: [["Billed", "values", C.indigo]], categories: "labels" },

  projects:     { source: "extra", id: "projects",      title: "Project Budget vs Actual Spend", kind: "bar",  series: [["Budget", "budget", C.indigo], ["Actual", "actual", C.orange]], categories: "labels" },
  stock:        { source: "extra", id: "stock",         title: "Stock Value by Item",            kind: "hbar", series: [["Stock value", "values", C.sky]], categories: "labels" },
  feesByClass:  { source: "extra", id: "fees_by_class", title: "Fees by Class — Collected vs Outstanding", kind: "bar", series: [["Collected", "collected", C.green], ["Outstanding", "outstanding", C.red]], categories: "labels", stacked: true },
  feesByTerm:   { source: "extra", id: "fees_by_term",  title: "Collection by Term",             kind: "bar",  series: [["Billed", "billed", C.indigo], ["Collected", "collected", C.green]], categories: "labels" },
};

/**
 * Hero tiles — the three figures a given business checks first.
 *   key    a `summary` field
 *   spark  path into the payload for the 12-point series drawn behind it
 *   period a `periods` field for the this-month / this-week line (optional)
 */
const HERO = {
  revenue:     { key: "total_revenue",    label: "Total Revenue",     period: "total_revenue",  spark: ["charts", "rve", "revenue"],  tone: "up" },
  expenses:    { key: "total_expenses",   label: "Total Expenses",    period: "total_expenses", spark: ["charts", "rve", "expenses"], tone: "down" },
  cash:        { key: "cash_balance",     label: "Cash & Bank",       period: "cash_balance",   spark: ["charts", "cfm", "inflow"] },
  profit:      { key: "net_profit",       label: "Net Profit",        period: "net_profit",     spark: ["charts", "npt", "profit"],   tone: "up" },
  collected:   { key: "fees_collected",   label: "Fees Collected",    period: "fees_collected", spark: ["extra", "fees_by_term", "collected"], tone: "up" },
  outstanding: { key: "fees_outstanding", label: "Fees Outstanding",                            spark: ["extra", "fees_by_class", "outstanding"], tone: "down" },
  grossProfit: { key: "gross_profit",     label: "Gross Profit",      period: "total_revenue",  spark: ["charts", "rve", "revenue"],  tone: "up" },
  stock:       { key: "stock_value",      label: "Stock on Hand",                               spark: ["extra", "stock", "values"] },
  receivables: { key: "outstanding_ar",   label: "Unpaid Receivables",                          spark: ["charts", "ar", "amounts"],   tone: "down" },
};

/** Quick-access destinations. Label only; the card is a plain link. */
const LINKS = {
  accounts:   { label: "Chart of Accounts",             link: "/accounting/accounts" },
  journal:    { label: "Journal Entries",               link: "/accounting/journal" },
  sales:      { label: "Sales Invoices",                link: "/accounting/invoices?type=outgoing" },
  purchases:  { label: "Purchase Invoices",             link: "/accounting/invoices?type=incoming" },
  bank:       { label: "Bank Reconciliation",           link: "/accounting/bank/reconcile" },
  trial:      { label: "Trial Balance",                 link: "/accounting/reports/trial-balance" },
  audit:      { label: "Audit Trail",                   link: "/accounting/audit" },
  budget:     { label: "Budget",                        link: "/accounting/budget" },
  reports:    { label: "Financial Reports",             link: "/accounting/reports" },
  payroll:    { label: "Payroll",                       link: "/accounting/payroll" },
  fees:       { label: "Fees Dashboard",                link: "/accounting/fees/dashboard" },
  students:   { label: "Student Fees",                  link: "/accounting/fees" },
  structure:  { label: "Fee Structure",                 link: "/accounting/fees/structure" },
  salesItems: { label: "Products / Services",           link: "/accounting/items/sales" },
  purchItems: { label: "Purchase Items",                link: "/accounting/items/purchase" },
  projects:   { label: "Projects & Cost Centres",       link: "/accounting/projects" },
  budgetVA:   { label: "Budget vs Actual",              link: "/accounting/budget/vs-actual" },
  assets:     { label: "Fixed Assets",                  link: "/accounting/assets" },
  tax:        { label: "Tax Filings",                   link: "/accounting/tax" },
  contacts:   { label: "Contacts",                      link: "/accounting/contacts" },
};

const GENERIC = {
  label: "Accounting Dashboard",
  blurb: "Real-time financial overview: profitability, cash position, receivables and payables for the selected financial year.",
  heroes: [HERO.revenue, HERO.expenses, HERO.cash],
  overview: [K.profit, K.margin, K.workingCap, K.drafts],
  kpis: [K.revenue, K.expenses, K.profit, K.margin, K.cash, K.ar, K.ap, K.liabilities, K.journals, K.drafts],
  charts: [
    { id: "rve", col: "col-lg-7" }, { id: "npt", col: "col-lg-5" },
    { id: "cfm", col: "col-lg-7" }, { id: "eb", col: "col-lg-5" },
    { id: "ar", col: "col-lg-4" }, { id: "ap", col: "col-lg-4" }, { id: "inv", col: "col-lg-4" },
  ],
  pairedChart: "tc",
  quickLinks: [LINKS.accounts, LINKS.journal, LINKS.sales, LINKS.purchases, LINKS.bank, LINKS.trial, LINKS.audit, LINKS.budget],
};

export const DASHBOARDS = {
  generic: GENERIC,

  school: {
    label: "School Finance Dashboard",
    blurb: "Fee collection, outstanding balances by class and term, alongside the school's overall financial position.",
    heroes: [HERO.collected, HERO.outstanding, HERO.cash],
    overview: [K.termStudents, K.employees, K.archivedOwing, K.collectionRate, K.openBills, K.feesWaived],
    kpis: [
      K.feesBilled, K.feesCollected, K.feesOutstanding, K.collectionRate,
      K.students, K.termStudents, K.openBills, K.feesWaived,
      K.archivedOwing, K.cash, K.revenue, K.expenses,
      K.profit, K.employees,
    ],
    charts: [
      { id: "feesByClass", col: "col-lg-7" }, { id: "feesByTerm", col: "col-lg-5" },
      { id: "rve", col: "col-lg-7" }, { id: "cfm", col: "col-lg-5" },
      { id: "rb", col: "col-lg-6" }, { id: "eb", col: "col-lg-6" },
    ],
    pairedChart: "ar",
    quickLinks: [LINKS.fees, LINKS.students, LINKS.structure, LINKS.payroll, LINKS.journal, LINKS.bank, LINKS.reports, LINKS.budget],
  },

  trading: {
    label: "Trading Dashboard",
    blurb: "Gross margin, stock position and the receivables/payables cycle that a buy-and-sell business runs on.",
    heroes: [HERO.revenue, HERO.grossProfit, HERO.stock],
    overview: [K.grossMargin, K.cogs, K.lowStock, K.workingCap],
    kpis: [
      K.revenue, K.cogs, K.grossProfit, K.grossMargin,
      K.stockValue, K.lowStock, K.ar, K.ap,
      K.cash, K.expenses, K.profit, K.overdueAr,
    ],
    charts: [
      { id: "rve", col: "col-lg-7" }, { id: "npt", col: "col-lg-5" },
      { id: "stock", col: "col-lg-6" }, { id: "eb", col: "col-lg-6" },
      { id: "ar", col: "col-lg-4" }, { id: "ap", col: "col-lg-4" }, { id: "inv", col: "col-lg-4" },
    ],
    pairedChart: "tc",
    quickLinks: [LINKS.salesItems, LINKS.purchItems, LINKS.sales, LINKS.purchases, LINKS.contacts, LINKS.bank, LINKS.trial, LINKS.tax],
  },

  manufacturing: {
    label: "Manufacturing Dashboard",
    blurb: "Production cost, stock and job/cost-centre performance against budget.",
    heroes: [HERO.revenue, HERO.grossProfit, HERO.stock],
    overview: [K.grossMargin, K.cogs, K.projects, K.projectBudget],
    kpis: [
      K.revenue, K.cogs, K.grossProfit, K.grossMargin,
      K.stockValue, K.lowStock, K.projects, K.projectBudget,
      K.cash, K.ap, K.profit, K.employees,
    ],
    charts: [
      { id: "rve", col: "col-lg-7" }, { id: "npt", col: "col-lg-5" },
      { id: "projects", col: "col-lg-7" }, { id: "stock", col: "col-lg-5" },
      { id: "eb", col: "col-lg-6" }, { id: "cfm", col: "col-lg-6" },
    ],
    pairedChart: "ap",
    quickLinks: [LINKS.purchItems, LINKS.salesItems, LINKS.projects, LINKS.budgetVA, LINKS.purchases, LINKS.assets, LINKS.payroll, LINKS.trial],
  },

  services: {
    label: "Services Dashboard",
    blurb: "Project profitability, billing pipeline and the cash cycle of a time-and-materials business.",
    heroes: [HERO.revenue, HERO.profit, HERO.receivables],
    overview: [K.margin, K.projects, K.projectBudget, K.overdueAr, K.employees],
    kpis: [
      K.revenue, K.expenses, K.profit, K.margin,
      K.projects, K.projectBudget, K.ar, K.overdueAr,
      K.cash, K.ap, K.employees, K.drafts,
    ],
    charts: [
      { id: "projects", col: "col-lg-7" }, { id: "npt", col: "col-lg-5" },
      { id: "rve", col: "col-lg-7" }, { id: "eb", col: "col-lg-5" },
      { id: "ar", col: "col-lg-6" }, { id: "inv", col: "col-lg-6" },
    ],
    pairedChart: "tc",
    quickLinks: [LINKS.projects, LINKS.sales, LINKS.contacts, LINKS.budgetVA, LINKS.payroll, LINKS.bank, LINKS.reports, LINKS.tax],
  },

  ngo: {
    label: "NGO Finance Dashboard",
    blurb: "Grant and project burn against budget, fund position and the expense picture donors ask about.",
    heroes: [HERO.revenue, HERO.expenses, HERO.cash],
    overview: [K.projects, K.projectBudget, K.workingCap, K.liabilities, K.employees, K.journals],
    kpis: [
      K.revenue, K.expenses, K.projects, K.projectBudget,
      K.cash, K.workingCap, K.liabilities, K.ap,
      K.employees, K.journals, K.drafts, K.profit,
    ],
    charts: [
      { id: "projects", col: "col-lg-7" }, { id: "eb", col: "col-lg-5" },
      { id: "cfm", col: "col-lg-7" }, { id: "ap", col: "col-lg-5" },
    ],
    pairedChart: "rve",
    quickLinks: [LINKS.projects, LINKS.budgetVA, LINKS.budget, LINKS.reports, LINKS.purchases, LINKS.payroll, LINKS.audit, LINKS.bank],
  },

  hospitality: {
    label: "Hospitality Dashboard",
    blurb: "Revenue mix across rooms and F&B, supplies on hand, and the daily cash position.",
    heroes: [HERO.revenue, HERO.grossProfit, HERO.cash],
    overview: [K.grossMargin, K.cogs, K.stockValue, K.lowStock, K.employees],
    kpis: [
      K.revenue, K.cogs, K.grossProfit, K.grossMargin,
      K.cash, K.stockValue, K.lowStock, K.ap,
      K.expenses, K.profit, K.employees, K.ar,
    ],
    charts: [
      { id: "rve", col: "col-lg-7" }, { id: "cfm", col: "col-lg-5" },
      { id: "stock", col: "col-lg-6" }, { id: "eb", col: "col-lg-6" },
    ],
    pairedChart: "ap",
    quickLinks: [LINKS.salesItems, LINKS.purchItems, LINKS.sales, LINKS.purchases, LINKS.payroll, LINKS.bank, LINKS.reports, LINKS.tax],
  },

  healthcare: {
    label: "Clinic Finance Dashboard",
    blurb: "Patient billing and collections, consumables on hand, and the practice's overall position.",
    heroes: [HERO.revenue, HERO.receivables, HERO.cash],
    overview: [K.margin, K.overdueAr, K.stockValue, K.lowStock, K.employees],
    kpis: [
      K.revenue, K.ar, K.overdueAr, K.collectionRate,
      K.stockValue, K.lowStock, K.cash, K.ap,
      K.expenses, K.profit, K.margin, K.employees,
    ],
    charts: [
      { id: "rve", col: "col-lg-7" }, { id: "npt", col: "col-lg-5" },
      { id: "ar", col: "col-lg-6" }, { id: "inv", col: "col-lg-6" },
      { id: "eb", col: "col-lg-6" }, { id: "stock", col: "col-lg-6" },
    ],
    pairedChart: "tc",
    quickLinks: [LINKS.sales, LINKS.contacts, LINKS.purchItems, LINKS.purchases, LINKS.payroll, LINKS.bank, LINKS.reports, LINKS.tax],
  },
};

/** Never blow up on an industry the frontend hasn't got a layout for. */
export const dashboardFor = (industry) => DASHBOARDS[industry] || GENERIC;

export default DASHBOARDS;
